import React from 'react';

import { useFormContext } from '@hero/ui-kit/inputs/Form';
import Input from '@hero/ui-kit/inputs/Input';
import Option from '@hero/ui-kit/inputs/Option';
import Select from '@hero/ui-kit/inputs/Select';
import TextArea from '@hero/ui-kit/inputs/TextArea';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import {
    CONTACT_METHODS,
    CONTACT_TIMES,
    PAIRING_STATUSES,
    SupplementalFormValues
} from '../../utils/supplemental';
import * as Style from './style.module.scss';

// ui-kit's Select only writes non-empty values to the form, so "Not set" needs a stand-in.
const UNSET = '__unset__';

type SupplementalSelectName =
    | 'preferred_contact_method'
    | 'preferred_contact_time'
    | 'device_pairing_status';

interface SupplementalSelectProps {
    name: SupplementalSelectName;
    displayName: string;
    options: string[];
    savedValue: string;
    disabled: boolean;
}

/**
 * ui-kit's Select keeps its own selection, seeded once from `defaultValue`, and writes it
 * back without marking the field dirty. So seed it from the saved value, remount when that
 * changes, and do the dirty-aware form write here.
 */
const SupplementalSelect: React.FC<SupplementalSelectProps> = ({
    name,
    displayName,
    options,
    savedValue,
    disabled
}) => {
    const { setValue } = useFormContext();

    // The backend takes free strings, so a saved value may be outside the documented list.
    const values = savedValue && !options.includes(savedValue) ? [...options, savedValue] : options;

    return (
        <div className={Style.select}>
            <Select
                key={savedValue}
                name={name}
                displayName={displayName}
                disabled={disabled}
                defaultValue={savedValue || UNSET}
                onChange={(value) =>
                    setValue(name, value === UNSET ? '' : value, { shouldDirty: true })
                }
            >
                {values.map((value) => (
                    <Option
                        key={value || UNSET}
                        value={value || UNSET}
                        labelText={value || 'Not set'}
                    />
                ))}
            </Select>
        </div>
    );
};

/** What `ContactAndCaregiverFields` renders, for callers that save only what they show. */
export const CONTACT_AND_CAREGIVER_FIELDS = [
    'preferred_contact_method',
    'preferred_contact_time',
    'caregiver_first_name',
    'caregiver_last_name',
    'caregiver_relationship'
] as const;

interface ContactAndCaregiverFieldsProps {
    savedValues: SupplementalFormValues;
    disabled: boolean;
}

export const ContactAndCaregiverFields: React.FC<ContactAndCaregiverFieldsProps> = ({
    savedValues,
    disabled
}) => (
    <>
        <SupplementalSelect
            name="preferred_contact_method"
            displayName="Preferred contact method"
            options={CONTACT_METHODS}
            savedValue={savedValues.preferred_contact_method}
            disabled={disabled}
        />
        <SupplementalSelect
            name="preferred_contact_time"
            displayName="Preferred contact time"
            options={CONTACT_TIMES}
            savedValue={savedValues.preferred_contact_time}
            disabled={disabled}
        />
        <Input disabled={disabled} name="caregiver_first_name" displayName="Caregiver first name" />
        <Input disabled={disabled} name="caregiver_last_name" displayName="Caregiver last name" />
        <Input
            disabled={disabled}
            name="caregiver_relationship"
            displayName="Caregiver relationship"
        />
    </>
);

interface SupplementalFormProps {
    savedValues: SupplementalFormValues;
    canEdit?: boolean;
    errorMessage?: string;
}

const SupplementalForm: React.FC<SupplementalFormProps> = ({
    savedValues,
    canEdit = true,
    errorMessage
}) => {
    const disabled = !canEdit;

    return (
        <Section noDefaultPadding>
            <div
                style={{
                    width: '100%',
                    textAlign: 'left',
                    marginBottom: '3.6rem',
                    marginTop: '0.4rem'
                }}
            >
                <P noDefaultMargin strong size="large" styles={{ marginBottom: '1.2rem' }}>
                    Additional
                </P>
            </div>

            {/* Fields stay mounted while loading: resetField ignores unregistered fields. */}
            <ContactAndCaregiverFields savedValues={savedValues} disabled={disabled} />
            <Input
                disabled={disabled}
                name="caregiver_phone"
                displayName="Caregiver phone"
                type="tel"
            />
            <Input
                disabled={disabled}
                name="caregiver_email"
                displayName="Caregiver email"
                type="email"
            />
            <SupplementalSelect
                name="device_pairing_status"
                displayName="Device pairing status"
                options={PAIRING_STATUSES}
                savedValue={savedValues.device_pairing_status}
                disabled={disabled}
            />
            {/*<StandaloneCheckbox
                disabled={disabled}
                name="hero_ai_opt_in"
                labelText="Opted into Hero AI features"
            />*/}
            <TextArea disabled={disabled} name="notes" displayName="Sticky note" rows={3} />

            {errorMessage ? (
                <P size="small" noDefaultMargin styles={{ color: 'var(--color-fail-alpha)' }}>
                    {errorMessage}
                </P>
            ) : null}
        </Section>
    );
};

export default SupplementalForm;
